
import { useState, useEffect } from 'react';

export default function ContactPage() {
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Carousel and form state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const heroSlides = [
    {
      title: "Premium Mango Excellence",
      subtitle: "Experience our commitment to delivering the finest mango pudding with premium quality ingredients",
      image: "https://public.readdy.ai/ai/img_res/dd8263848d44f66ebb70b22555a3092b.jpg"
    },
    {
      title: "Get in Touch",
      subtitle: "We'd love to hear from you! Reach out with any questions, feedback, or special requests",
      image: "https://readdy.ai/api/search-image?query=Professional%20mango%20pudding%20manufacturing%20facility%20with%20modern%20equipment%2C%20clean%20production%20line%2C%20quality%20control%20processes%2C%20food%20safety%20standards%2C%20industrial%20kitchen%20setting%2C%20premium%20dessert%20production&width=1200&height=500&seq=2&orientation=landscape"
    },
    {
      title: "Quality Assurance",
      subtitle: "Our state-of-the-art facilities ensure the highest quality standards for every product",
      image: "https://readdy.ai/api/search-image?query=Food%20quality%20control%20laboratory%20with%20scientists%20testing%20mango%20pudding%20samples%2C%20professional%20food%20testing%20equipment%2C%20HACCP%20certification%2C%20ISO%20standards%2C%20clean%20laboratory%20environment&width=1200&height=500&seq=3&orientation=landscape"
    },
    {
      title: "Sustainable Mango Production",
      subtitle: "Eco-friendly mango sourcing and sustainable practices for a better tomorrow",
      image: "https://readdy.ai/api/search-image?query=Sustainable%20mango%20farming%20and%20processing%20facility%20with%20fresh%20mango%20orchards%2C%20eco-friendly%20mango%20packaging%20materials%2C%20green%20manufacturing%20processes%2C%20environmental%20responsibility%2C%20organic%20mango%20production&width=1200&height=500&seq=4&orientation=landscape"
    },
    {
      title: "Research & Development",
      subtitle: "Continuous innovation to bring you the finest mango dessert experiences",
      image: "https://readdy.ai/api/search-image?query=Professional%20food%20research%20and%20development%20laboratory%20with%20food%20scientists%20in%20white%20coats%20developing%20new%20mango%20pudding%20recipes%2C%20modern%20food%20technology%20equipment%2C%20product%20development%20facility%2C%20food%20innovation%20center&width=1200&height=500&seq=5&orientation=landscape"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  // Original contactInfo array
  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Phone',
      details: '(571) 531-8278',
      description: 'Mon-Fri 9AM-6PM EST'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      details: 'support@mangotreasure.com',
      description: 'We reply within 24 hours'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Address',
      details: '#303, 1952 Gallows Rd, Vienna, VA, United States',
      description: 'Visit our headquarters'
    },
    {
      icon: 'ri-time-line',
      title: 'Business Hours',
      details: 'Mon-Fri: 9AM-6PM',
      description: 'Saturday: 10AM-4PM'
    }
  ];

  // Original faqs array
  const faqs = [
    {
      question: 'How long do your products stay fresh?',
      answer: 'Our mango puddings stay fresh for up to 7 days when refrigerated properly. We recommend consuming within 3-5 days for the best taste experience.'
    },
    {
      question: 'Do you offer bulk orders for events?',
      answer: 'Yes! We offer special pricing for bulk orders of 50+ units. Please contact us at least 48 hours in advance for event catering.'
    },
    {
      question: 'Are your products suitable for people with dietary restrictions?',
      answer: 'Our products are gluten-free and made with natural ingredients. Please check individual product labels for specific allergen information.'
    },
    {
      question: 'What is your return policy?',
      answer: "We offer a 100% satisfaction guarantee. If you're not completely satisfied with your order, contact us within 24 hours of delivery for a full refund."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-orange-50">
      {/* Free Shipping Banner */}
      <section className="bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-3">
            <i className="ri-truck-line text-2xl"></i>
            <span className="text-lg font-semibold">
              FREE SHIPPING on orders over $50!
            </span>
            <i className="ri-gift-line text-2xl"></i>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <nav className="bg-gradient-to-r from-rose-100/95 via-pink-100/95 to-orange-100/95 backdrop-blur-md shadow-xl sticky top-0 z-50 border-b border-rose-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/e97325bca996f1ce3b54a2c00c06237a/b38163c3c482e773cae655e9f9113361.png"
                alt="Mango Treasure Logo"
                className="h-16 w-auto mr-4 drop-shadow-lg"
              />
              <span
                className="text-3xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                Mango Treasure
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                >
                  Shop Products
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                >
                  Our Story
                </a>
                <a
                  className="text-rose-700 hover:text-rose-900 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                >
                  Contact
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg flex items-center space-x-2 font-semibold"
                  href="/cart"
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-rose-600 p-3 rounded-lg shadow-lg transition-all duration-300 cursor-pointer border border-rose-100"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-xl border border-rose-200/50">
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Shop Products
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Our Story
                </a>
                <a
                  className="text-rose-700 hover:text-rose-900 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 cursor-pointer shadow-lg flex items-center space-x-2 font-semibold justify-center mt-4"
                  href="/cart"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Carousel Section */}
      <section className="relative h-[500px] overflow-hidden">
        <div className="relative w-full h-full">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === index ? 'opacity-100' : 'opacity-0'}`}
            >
              <div
                className="w-full h-full bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('${slide.image}')`
                }}
              >
                <div className="flex items-center justify-center h-full">
                  <div className="text-center px-4 max-w-4xl mx-auto">
                    <h1 className="text-5xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                      {slide.title}
                    </h1>
                    <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
                      {slide.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-rose-600 p-3 rounded-full shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer border border-rose-100"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-rose-600 p-3 rounded-full shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer border border-rose-100"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${currentSlide === index ? 'bg-rose-500 scale-125' : 'bg-white/60 hover:bg-white/80'}`}
            />
          ))}
        </div>

        {/* Slide Counter */}
        <div className="absolute bottom-6 right-6 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-rose-600 font-medium border border-rose-100">
          {currentSlide + 1} / {heroSlides.length}
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-8">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-rose-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-rose-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-rose-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-rose-700 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-sm pr-8 cursor-pointer bg-white/80 backdrop-blur-sm"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="product">Product Information</option>
                      <option value="order">Order Support</option>
                      <option value="wholesale">Wholesale Inquiry</option>
                      <option value="feedback">Feedback</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-rose-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-sm resize-none bg-white/80 backdrop-blur-sm"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  <p className="text-sm text-rose-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white py-4 rounded-lg text-lg font-semibold hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-8">Contact Information</h2>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-rose-800 mb-2">Our Location</h3>
                    <p className="text-rose-600">
                      #303, 1952 Gallows Rd<br />
                      Vienna, VA, United States
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-rose-800 mb-2">Phone</h3>
                    <p className="text-rose-600">
                      <a href="tel:+1-571-531-8278" className="hover:text-rose-800 transition-colors cursor-pointer">
                        (571) 531-8278
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-rose-800 mb-2">Email</h3>
                    <p className="text-rose-600">
                      <a href="mailto:support@mangotreasure.com" className="hover:text-rose-800 transition-colors cursor-pointer">
                        support@mangotreasure.com
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-rose-800 mb-2">Business Hours</h3>
                    <p className="text-rose-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 4:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-12">
                <h3 className="text-lg font-semibold text-rose-800 mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a href="#" className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 cursor-pointer shadow-lg">
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 cursor-pointer shadow-lg">
                    <i className="ri-instagram-fill text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 cursor-pointer shadow-lg">
                    <i className="ri-twitter-fill text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 cursor-pointer shadow-lg">
                    <i className="ri-linkedin-fill text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gradient-to-r from-rose-50 via-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-rose-100/50">
                <div className="w-16 h-16 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className={`${info.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-rose-800 mb-2">{info.title}</h3>
                <p className="text-rose-700 font-medium mb-1">{info.details}</p>
                <p className="text-rose-600 text-sm">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-gradient-to-br from-rose-50/80 via-pink-50/80 to-orange-50/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-rose-100/50">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-6">Send us a Message</h2>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-rose-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-rose-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-rose-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-rose-700 mb-2">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm pr-8 cursor-pointer bg-white/80 backdrop-blur-sm"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="order">Order Support</option>
                      <option value="bulk">Bulk Orders</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-rose-700 mb-2">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm resize-none bg-white/80 backdrop-blur-sm"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  <p className="text-sm text-rose-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white py-4 rounded-lg text-lg font-semibold hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Map and Additional Info */}
            <div className="space-y-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-rose-100/50">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-4">Visit Our Location</h3>
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3048.4037!2d-118.2437!3d34.0522!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDAzJzA4LjAiTiAxMTjCsDE0JzM3LjAiVw!5e0!3m2!1sen!2sus!4v1234567890"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mango Treasure Location"
                  ></iframe>
                </div>
                <div className="mt-4 p-4 bg-gradient-to-r from-rose-50 via-pink-50 to-orange-50 rounded-lg border border-rose-100">
                  <p className="text-rose-700 font-medium">#303, 1952 Gallows Rd</p>
                  <p className="text-rose-600">Vienna, VA, United States</p>
                  <p className="text-rose-600 text-sm mt-2">Free parking available</p>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-rose-100/50">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-4">Quick Response</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <i className="ri-time-line text-rose-500 text-xl"></i>
                    <span className="text-rose-700">Email responses within 24 hours</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-phone-line text-rose-500 text-xl"></i>
                    <span className="text-rose-700">Phone support Mon-Fri 9AM-6PM</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-chat-smile-line text-rose-500 text-xl"></i>
                    <span className="text-rose-700">Live chat available on website</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-r from-rose-50 via-pink-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-rose-600">
              Quick answers to common questions about our products and services
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-rose-100/50">
              <h3 className="text-lg font-semibold text-rose-800 mb-3">What makes Mango Treasure pudding special?</h3>
              <p className="text-rose-600">Our pudding is made with 100% natural mango pulp, contains only 105 calories per serving, and uses no artificial preservatives or additives. We focus on premium quality and authentic taste.</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-rose-100/50">
              <h3 className="text-lg font-semibold text-rose-800 mb-3">Do you offer wholesale pricing?</h3>
              <p className="text-rose-600">Yes! We offer competitive wholesale pricing for retailers, restaurants, and bulk orders. Please contact us directly for wholesale inquiries and pricing information.</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-rose-100/50">
              <h3 className="text-lg font-semibold text-rose-800 mb-3">What is your shipping policy?</h3>
              <p className="text-rose-600">We offer free shipping on orders over $50 within the continental US. Orders are typically processed within 1-2 business days and delivered within 3-5 business days.</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-rose-100/50">
              <h3 className="text-lg font-semibold text-rose-800 mb-3">Are your products suitable for dietary restrictions?</h3>
              <p className="text-rose-600">Our mango pudding is naturally gluten‑free and vegetarian. We also offer sugar‑free options. Please check individual product labels for specific dietary information or contact us for detailed ingredient lists.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-rose-900 via-pink-900 to-orange-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="https://static.readdy.ai/image/e97325bca996f1ce3b54a2c00c06237a/b38163c3c482e773cae655e9f9113361.png"
                  alt="Mango Treasure Logo"
                  className="h-12 w-auto mr-3 drop-shadow-lg"
                />
                <h3
                  className="text-2xl font-bold"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  Mango Treasure
                </h3>
              </div>
              <p className="text-rose-200 mb-4">
                Premium mango pudding crafted with love and natural ingredients for the perfect dessert experience.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/">Home</a></li>
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/products">Products</a></li>
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/story">Our Story</a></li>
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/contact">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Customer Service</h4>
              <ul className="space-y-2">
                <li><a className="text-rose-200 hover:text-white transition-colors cursor-pointer" href="/orders">Track Orders</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">FAQ</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Shipping Info</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Returns</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Connect With Us</h4>
              <div className="flex space-x-4 mb-4">
                <a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-2xl"></i>
                </a>
                <a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-2xl"></i>
                </a>
                <a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-2xl"></i>
                </a>
              </div>
              <p className="text-rose-200 text-sm">Follow us for updates and special offers</p>
            </div>
          </div>
          <div className="border-t border-rose-800 mt-8 pt-8 text-center">
            <p className="text-rose-200">
              © 2024 Mango Treasure. All rights reserved. |
              <a href="https://readdy.ai/?origin=logo" className="text-rose-200 hover:text-white ml-2 transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
